"use client"

import * as React from "react"

declare global {
  interface Window {
    updateTheme: () => void
  }
}

type Theme = "dark" | "light"
type CustomTheme = "light" | "night" | "dark" | "system"

type ThemeContextType = {
  theme: Theme
  setTheme: React.Dispatch<React.SetStateAction<Theme>>
  customTheme: CustomTheme
  setCustomTheme: React.Dispatch<React.SetStateAction<CustomTheme>>
}

const STORAGE_KEY = "theme"
const CUSTOM_THEME_KEY = "app-theme"

function ThemeScript() {
  const scriptContent = `
    (function() {
      const STORAGE_KEY = "${STORAGE_KEY}";
      const CUSTOM_THEME_KEY = "${CUSTOM_THEME_KEY}";
      const DARK = "dark";
      const LIGHT = "light";

      const disableTransitions = () => {
        const style = document.createElement("style");
        style.textContent = "*,*:after,*:before{transition:none !important;}";
        document.head.appendChild(style);
        
        return () => {
          document.body.offsetHeight;
          setTimeout(() => document.head.removeChild(style), 1);
        };
      };

      const updateTheme = () => {
        const restoreTransitions = disableTransitions();
        const systemPreference = window.matchMedia("(prefers-color-scheme: dark)").matches ? DARK : LIGHT;
        const savedTheme = localStorage.getItem(STORAGE_KEY);
        const customTheme = localStorage.getItem(CUSTOM_THEME_KEY);
        
        // ✅ Jika custom theme = system, gunakan system preference
        const theme = customTheme === "system" 
          ? systemPreference 
          : (savedTheme ?? systemPreference);
        
        document.documentElement.classList.remove(DARK, LIGHT);
        document.documentElement.classList.add(theme);

        // ✅ Set data-theme attribute
        if (customTheme) {
          document.documentElement.setAttribute('data-theme', customTheme);
        }
        
        let metaColorScheme = document.querySelector('meta[name="color-scheme"]');
        if (!metaColorScheme) {
          metaColorScheme = document.createElement('meta');
          metaColorScheme.setAttribute('name', 'color-scheme');
          document.head.appendChild(metaColorScheme);
        }
        metaColorScheme.setAttribute('content', theme === DARK ? 'dark' : 'light');
        
        restoreTransitions();
      };

      updateTheme();
      
      // ✅ Listen system preference changes (penting untuk system mode)
      window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", (e) => {
        const customTheme = localStorage.getItem(CUSTOM_THEME_KEY);
        if (customTheme === "system") {
          updateTheme();
        }
      });
      
      window.updateTheme = updateTheme;
    })();
  `

  return <script dangerouslySetInnerHTML={{ __html: scriptContent }} />
}

const ThemeContext = React.createContext<ThemeContextType | undefined>(undefined)

const useThemeContext = () => {
  const context = React.useContext(ThemeContext)
  if (!context) {
    throw new Error("ThemeContext must be used within a ThemeProvider")
  }
  return context
}

function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [customTheme, setCustomTheme] = React.useState<CustomTheme>(() => {
    if (typeof window === "undefined") return "system"
    return (localStorage.getItem(CUSTOM_THEME_KEY) as CustomTheme) ?? "system"
  })

  const [theme, setTheme] = React.useState<Theme>(() => {
    if (typeof window === "undefined") return "light"

    const savedCustomTheme = localStorage.getItem(CUSTOM_THEME_KEY) as CustomTheme
    const systemPreference = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"

    // ✅ Jika custom theme = system, gunakan system preference
    if (savedCustomTheme === "system") {
      return systemPreference
    }

    const savedTheme = localStorage.getItem(STORAGE_KEY) as Theme | null
    return savedTheme ?? systemPreference
  })

  // ✅ Listen system preference changes
  React.useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")

    const handleChange = (e: MediaQueryListEvent) => {
      // ✅ Hanya update jika custom theme = system
      if (customTheme === "system") {
        setTheme(e.matches ? "dark" : "light")
      }
    }

    mediaQuery.addEventListener("change", handleChange)
    return () => mediaQuery.removeEventListener("change", handleChange)
  }, [customTheme])

  // ✅ Handle storage changes from other tabs
  React.useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        setTheme(e.newValue as Theme)
      }
      if (e.key === CUSTOM_THEME_KEY && e.newValue) {
        setCustomTheme(e.newValue as CustomTheme)
      }
    }

    window.addEventListener("storage", handleStorageChange)
    return () => window.removeEventListener("storage", handleStorageChange)
  }, [])

  // ✅ Update DOM when theme changes
  React.useEffect(() => {
    // ✅ Hanya save ke localStorage jika bukan system
    if (customTheme !== "system") {
      localStorage.setItem(STORAGE_KEY, theme)
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }

    localStorage.setItem(CUSTOM_THEME_KEY, customTheme)
    document.documentElement.setAttribute("data-theme", customTheme)

    if (typeof window !== "undefined" && window.updateTheme) {
      window.updateTheme()
    }
  }, [theme, customTheme])

  const value = React.useMemo(() => ({
    theme,
    setTheme,
    customTheme,
    setCustomTheme,
  }), [theme, customTheme])

  return (
    <ThemeContext value={value}>
      {children}
      <ThemeScript />
    </ThemeContext>
  )
}

export { ThemeProvider, useThemeContext }
